import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Tabs } from 'antd';
import { BankOutlined, UserOutlined, TagOutlined } from '@ant-design/icons';
import {
  listLlms,
  listPromptScreens,
} from 'src/modules/SuperAdmin/store/actions/promptsv2.action';
import { useLLMOverrides } from './useLLMOverrides';
import OrganizationLLMs from './OrganizationLLMs';
import PractitionerLLMs from './PractitionerLLMs';
import PlanLLMs from './PlanLLMs';
import { predefinedUseCases } from './config';
import { getFromStorage } from 'src/lib/storage';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';

const LLMs = () => {
  const dispatch = useDispatch();
  const {
    selectedOrganization,
    initializeTabConfiguration,
    getTabConfiguration,
  } = useLLMOverrides();

  // State for active sub-tab
  const [activeSubTab, setActiveSubTab] = useState('org');

  const [selectedOrganizationUUID, _setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || null;
    }
  );
  useEffect(() => {
    if (selectedOrganizationUUID) {
      dispatch(getAllActiveOrganizationAction());
    }
  }, [dispatch, selectedOrganizationUUID]);

  // Load data on component mount
  useEffect(() => {
    dispatch(listLlms());
    dispatch(listPromptScreens());
  }, [dispatch]);

  // Initialize organization tab configuration when switching to it
  useEffect(() => {
    if (activeSubTab === 'org') {
      const currentTabConfig = getTabConfiguration('org');
      const isEmpty = Object.keys(currentTabConfig).length === 0;
      if (isEmpty) {
        initializeTabConfiguration('org', predefinedUseCases);
      }
    }
  }, [activeSubTab, initializeTabConfiguration, getTabConfiguration]);

  // Create tab items
  const tabItems = useMemo(
    () => [
      {
        key: 'org',
        label: (
          <span>
            <BankOutlined className="mr-2" />
            Organization
          </span>
        ),
        children: (
          <OrganizationLLMs selectedOrganization={selectedOrganization} />
        ),
      },
      {
        key: 'practitioner',
        label: (
          <span>
            <UserOutlined className="mr-2" />
            Practitioner
          </span>
        ),
        children: (
          <PractitionerLLMs selectedOrganization={selectedOrganization} />
        ),
      },
      {
        key: 'plan',
        label: (
          <span>
            <TagOutlined className="mr-2" />
            Plan
          </span>
        ),
        children: <PlanLLMs selectedOrganization={selectedOrganization} />,
      },
    ],
    [selectedOrganization]
  );

  return (
    <div className="space-y-4">
      {/* Warning message if no organization selected (except for plan level) */}
      {!selectedOrganization && activeSubTab !== 'plan' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="text-yellow-800">
            Please select an organization to configure LLM overrides.
          </div>
        </div>
      )}

      {/* Configuration Tabs */}
      <Tabs
        activeKey={activeSubTab}
        onChange={setActiveSubTab}
        items={tabItems}
        size="large"
        className="llm-configuration-tabs"
      />
    </div>
  );
};

export default LLMs;
